import styled, { css } from 'styled-components'

import { BackButton } from 'components/BackButton'

import { Color, SECTIONED_PROGRESS_HEADER_HEIGHT } from 'root-constants'

import {
  HEADER_BORDER_COLOR,
  HEADER_VARIANTS,
  THeaderVariant,
} from './constants'

const variantStyles = {
  [HEADER_VARIANTS.SECONDARY]: css`
    font-family: 'Playfair Display', serif;
    font-size: 24px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -0.95px;
    color: ${Color.GOLD_MEDIUM};
  `,
  [HEADER_VARIANTS.PRIMARY]: css`
    font-family: 'Inter', sans-serif;
    font-size: 18px;
    font-weight: 400;
    line-height: 18px;
    color: ${Color.INK_DARKEST};
  `,
}

const GLASS_BACKGROUND_COLOR = 'rgba(255, 255, 255, 0.25)'
const GLASS_BLUR = 'blur(12px)'

const getBorderColor = ({
  $background,
  $borderColor,
}: {
  $background?: string
  $borderColor?: string
}) => {
  if ($borderColor) return $borderColor
  if ($background === Color.WHITE) return HEADER_BORDER_COLOR

  return $background ?? Color.SKY_BASE
}

const getHeaderPosition = ({
  $isHeaderOverlay,
  $hasGlassEffect,
}: {
  $isHeaderOverlay?: boolean
  $hasGlassEffect?: boolean
}) => {
  if (!$isHeaderOverlay) return 'sticky'

  return $hasGlassEffect ? 'fixed' : 'absolute'
}

export const StyledHeaderV2 = {
  Wrapper: styled.header<{
    $background?: string
    $isHeaderOverlay?: boolean
    $hasGlassEffect?: boolean
    $borderColor?: string
    $hasSectionedProgress?: boolean
  }>`
    position: ${getHeaderPosition};
    top: ${({ $isHeaderOverlay }) => ($isHeaderOverlay ? '0' : 'auto')};
    z-index: ${({ $isHeaderOverlay }) => ($isHeaderOverlay ? 10 : 'auto')};

    width: 100%;
    height: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;

    background-color: ${({ $hasGlassEffect }) =>
      $hasGlassEffect ? GLASS_BACKGROUND_COLOR : 'transparent'};
    -webkit-backdrop-filter: ${({ $hasGlassEffect }) =>
      $hasGlassEffect ? GLASS_BLUR : 'none'};
    backdrop-filter: ${({ $hasGlassEffect }) =>
      $hasGlassEffect ? GLASS_BLUR : 'none'};
    border-bottom: 1px solid ${getBorderColor};
    padding: 12px 0;

    ${({ $hasSectionedProgress }) =>
      $hasSectionedProgress &&
      css`
        height: ${SECTIONED_PROGRESS_HEADER_HEIGHT}px;
        border-bottom: none;
        padding: 0;
      `}

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: -1;

      background: ${({ $background }) => $background || Color.SKY_BASE};
      opacity: ${({ $isHeaderOverlay }) => ($isHeaderOverlay ? 0.25 : 1)};
    }
  `,

  Text: styled.span<{ variant: THeaderVariant; $textColor?: string }>`
    ${({ variant }) => variantStyles[variant]}
    margin-left: auto;
    margin-right: auto;
    z-index: 1;

    ${({ $textColor }) =>
      $textColor &&
      css`
        color: ${$textColor};
      `}
  `,

  HeaderBackButton: styled(BackButton)`
    position: absolute;
    width: 24px;
    height: 24px;

    top: 50%;
    inset-inline-start: 18px;
    transform: translateY(-50%);
    z-index: 1;

    svg {
      :dir(rtl) {
        transform: scaleX(-1);
      }
    }
  `,
}
